import React, { useEffect, useState, useCallback } from "react";
import {
  isNativeApp,
  biometricPreference,
  biometricAuthenticate,
} from "@/lib/biometric";
import { Fingerprint, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Blocking overlay shown when running as a native Capacitor app AND the user
 * has enabled biometric auth. On the web, this component renders nothing so
 * the browser experience stays identical.
 */
export default function BiometricLock({ children }) {
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState(true);
  const [gateActive, setGateActive] = useState(false);
  const [error, setError] = useState(null);

  const tryUnlock = useCallback(async () => {
    setError(null);
    try {
      await biometricAuthenticate();
      setUnlocked(true);
    } catch (e) {
      setError(e?.message || "Autenticación cancelada");
      setUnlocked(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const active = isNativeApp() && biometricPreference();
    setGateActive(active);
    if (active) {
      setUnlocked(false);
      tryUnlock();
    } else {
      setUnlocked(true);
    }
  }, [user, tryUnlock]);

  useEffect(() => {
    // "Bloquear ahora" from user menu re-triggers this gate without full logout
    const onLock = () => {
      if (!isNativeApp() || !biometricPreference()) return;
      setGateActive(true);
      setUnlocked(false);
      tryUnlock();
    };
    window.addEventListener("bio-lock", onLock);
    return () => window.removeEventListener("bio-lock", onLock);
  }, [tryUnlock]);

  if (!gateActive || unlocked) return children;

  return (
    <div
      data-testid="biometric-lock"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-[#0F291E] text-white p-8"
    >
      <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center">
        <Fingerprint className="w-10 h-10 text-white" />
      </div>
      <div className="text-center">
        <h1 className="font-display text-3xl font-semibold">Patrimonio</h1>
        <p className="text-white/70 mt-2 max-w-xs mx-auto text-sm">
          Autentícate con Face ID o huella para acceder a tus finanzas.
        </p>
      </div>
      {error && (
        <div className="flex items-center gap-2 text-[#F2C6B5] text-sm">
          <ShieldAlert className="w-4 h-4" /> {error}
        </div>
      )}
      <button
        type="button"
        onClick={tryUnlock}
        data-testid="biometric-retry"
        className="px-6 py-3 rounded-xl bg-white text-[#0F291E] font-semibold hover:bg-white/90"
      >
        Intentar de nuevo
      </button>
    </div>
  );
}
